import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { signInAppCSS } from '../constants';
import '../sass/clear.scss';

const FALLBACK_PATH = '/?oauth=true';
const FALLBACK_HREF = `/sign-in${FALLBACK_PATH}`;

export function getBackTarget() {
  try {
    const url = new URL(document.referrer);
    if (url.origin === window.location.origin) {
      return {
        href: `${url.pathname}${url.search}${url.hash}`,
        canGoBack: true,
      };
    }
  } catch (e) {
    // Fall through to the default case
  }
  return { href: FALLBACK_HREF, canGoBack: false };
}

export default function ClearLanding({ location, router }) {
  useEffect(() => {
    document.title = 'Sign in with CLEAR | Veterans Affairs';
  }, [location, router]);

  const backTarget = getBackTarget();

  const handleBackClick = e => {
    e.preventDefault();
    if (backTarget.canGoBack) {
      router.goBack();
    } else {
      router.push(FALLBACK_PATH);
    }
  };

  return (
    <>
      <style>{signInAppCSS}</style>
      <section className="login login-page">
        <div className="container">
          <p>
            <va-link
              back
              href={backTarget.href}
              text="Back to previous page"
              onClick={handleBackClick}
            />
          </p>
          <div className="clear-landing">
            <h1>Sign in to VA.gov with CLEAR</h1>
            <p className="clear-landing-intro va-introtext">
              You can now use CLEAR to sign in to VA.gov. After you create your
              account, you can sign in by using your smartphone camera.
            </p>

            <va-alert status="info" visible class="clear-landing-info">
              <h2 slot="headline">We’re not changing other sign-in options</h2>
              <p className="vads-u-margin-y--0">
                If you’re already using <strong>ID.me</strong> or{' '}
                <strong>Login.gov</strong>, you can keep using that account.
                CLEAR is just another option.
              </p>
            </va-alert>

            <h2>What to know about using CLEAR</h2>
            <ul className="clear-landing-list">
              <li>
                <strong>A CLEAR account is secure.</strong> CLEAR accounts meet
                the same security standards as ID.me and Login.gov accounts.
              </li>
              <li>
                <strong>Your VA benefits and care stay the same.</strong> Using
                a CLEAR account to sign in won’t affect your VA benefits or
                care.
              </li>
              <li>
                <strong>We don’t share your information.</strong> We don’t share
                any of your healthcare or other records with CLEAR.
              </li>
              <li>
                <strong>Signing in with CLEAR is free.</strong> There’s no cost
                for you to create a CLEAR account for VA.gov.
              </li>
            </ul>

            <h2>How to get started</h2>
            <p>
              You can use either of these 2 options to create a CLEAR account
              for VA.gov.
            </p>
            <h3>Online</h3>
            <p>
              You can create your account for VA.gov online. Here’s what you’ll
              need:
            </p>
            <ul className="clear-landing-list">
              <li>
                A smartphone with a working front-facing camera and updated
                software, <strong>and</strong>
              </li>
              <li>
                Your Social Security number, <strong>and</strong>
              </li>
              <li>A government-issued ID</li>
            </ul>
            <va-link-action
              href="/sign-in?type=clear_signup"
              text="Create an account with CLEAR"
              type="primary"
            />

            <h3>In person</h3>
            <p>
              Some VA health facilities offer in-person help from CLEAR
              Ambassadors.
            </p>
            <p>Here are the facilities where you can get help:</p>
            <ul className="clear-landing-links">
              <li>
                <va-link
                  href="/atlanta-health-care/locations/joseph-maxwell-cleland-atlanta-va-medical-center"
                  text="Joseph Maxwell Cleland Atlanta VA Medical Center"
                />
              </li>
              <li>
                <va-link
                  href="/new-york-harbor-health-care/locations/brooklyn-va-medical-center"
                  text="Brooklyn VA Medical Center"
                />
              </li>
              <li>
                <va-link
                  href="/orlando-health-care/locations/orlando-va-medical-center"
                  text="Orlando VA Medical Center"
                />
              </li>
              <li>
                <va-link
                  href="/palo-alto-health-care/locations/palo-alto-va-medical-center"
                  text="Palo Alto VA Medical Center"
                />
              </li>
              <li>
                <va-link
                  href="/caribbean-health-care/locations/san-juan-va-medical-center"
                  text="San Juan VA Medical Center"
                />
              </li>
              <li>
                <va-link
                  href="/washington-dc-health-care/locations/washington-va-medical-center"
                  text="Washington VA Medical Center"
                />
              </li>
            </ul>
            <p>Here’s what you'll need to bring:</p>
            <ul className="clear-landing-list">
              <li>
                A valid photo ID, <strong>and</strong>
              </li>
              <li>
                A phone that can receive text messages, <strong>and</strong>
              </li>
              <li>Your personal email address</li>
            </ul>
            <p>
              <strong>Note:</strong> You don't need your smartphone to set up an
              account in person.
            </p>

            <h2>Is this the same CLEAR that I can use at airports?</h2>
            <p>
              It’s the same company. Using CLEAR on VA.gov and other places
              outside of the airport is free. Using CLEAR lanes at airport
              security requires a separate, paid CLEAR+ membership.
            </p>

            <h2>How can I get direct support for my CLEAR account?</h2>
            <p>
              CLEAR offers support with online Frequently Asked Questions
              (FAQs). You can try finding the solution to your issue there.
            </p>
            <p>
              <va-link
                external
                href="https://www.clearme.com/support"
                text="Get help on the CLEAR website"
              />
            </p>
            <p>
              If you’re still having trouble, you can contact their member
              services team or use their chat support.
            </p>
            <p>
              <va-link
                external
                href="https://www.clearme.com/support/how-do-i-contact-clear"
                text="Contact CLEAR about your account on their website"
              />
            </p>

            <h2>Learn more</h2>
            <ul className="clear-landing-links">
              <li>
                <va-link
                  href="/resources/how-to-create-a-clear-account-for-va"
                  text="Get step-by-step instructions to create a free CLEAR account"
                />
              </li>
              <li>
                <va-link
                  href="/resources/support-for-common-sign-in-account-issues/#clear-account-issues"
                  text="Fix common sign-in problems"
                />
              </li>
              <li>
                <va-link
                  href="/resources/can-i-delete-my-idme-logingov-or-clear-account"
                  text="Delete a CLEAR account"
                />
              </li>
            </ul>
            <p className="clear-landing-help">
              <va-link
                external
                href="https://www.clearme.com/partner-detail-pages/veteran-affairs?utm_source=va&utm_medium=referral"
                text="Get help from CLEAR with setup and your CLEAR account on the CLEAR website"
              />
            </p>

            <h2>Need help?</h2>
            <p>
              Call our VA.gov technical support line for help at{' '}
              <va-telephone contact="8662793677" /> (
              <va-telephone contact="711" tty />
              ). We’re here Monday through Friday from 8 a.m. to 8 p.m. ET.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

ClearLanding.propTypes = {
  location: PropTypes.object,
  router: PropTypes.object,
};
